/**
 * React entry point mounted inside the <propelsia-booking> shadow DOM (or
 * inside a nested shadow root when in modal mode).
 *
 * Same offer as /book/[slug] and the public websites' booking-widget: « Prestations | Trajet » tabs
 * when the pro offers both, the ride tunnel straight away for a ride-only pro, « Course » cards of
 * the classic tunnel opening the ride tunnel, suspended-booking card.
 */

import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  BookingSuspendedCard,
  BookingTunnel,
  createBookingCallbacks,
  createRideCallbacks,
  fetchProfessionalPublic,
  readLinkCodes,
  resolveTunnelMode,
  rideInitialTripFromPrestation,
  RideTunnel,
  TunnelModeSwitch,
  type LinkCodes,
  type RideCallbacks,
  type RideInitialTrip,
  type TunnelMode,
} from '@cfixe-monorepo/shared-components';
import { REDIRECT_RETURN_PARAM } from '@cfixe-monorepo/shared-components/pro-payment/redirect-return';
import { WIDGET_FLAG_BASE_URL } from '../core';
import type {
  AddressAutocompletePrediction,
  AddressObject,
  BookingCallbacks,
  BookingFilterConfig,
  BookingPrestation,
  BookingTunnelProps,
} from '@cfixe-monorepo/shared-components/booking/types';
import {
  BookingSource,
  public_env,
  readCampaignIdFromSearch,
  translate,
  type PublicProfessionalResponseObject,
} from '@cfixe-monorepo/shared';

type CapInstance = { solve: () => Promise<{ success: boolean; token: string }>; reset: () => void };

async function getCaptchaToken(capRef: { current: CapInstance | null }): Promise<string> {
  if (!capRef.current) {
    const { default: Cap } = await import('@cap.js/widget');
    const endpoint = public_env.API_ENDPOINT.endsWith('/') ? public_env.API_ENDPOINT : `${public_env.API_ENDPOINT}/`;
    capRef.current = new Cap({ apiEndpoint: `${endpoint}public/captcha/` });
  }
  const result = await capRef.current.solve();
  if (!result.success || !result.token) throw new Error('Captcha failed');
  return result.token;
}

async function fetchAddressAutocomplete(
  query: string,
  token: string,
): Promise<{ predictions: { place_id: string; description: string }[] }> {
  const url = `${public_env.API_ENDPOINT}/public/address/autocomplete?query=${encodeURIComponent(query)}`;
  const res = await fetch(url, { headers: { 'x-cap-token': token } });
  if (!res.ok) throw new Error(`Autocomplete failed: ${res.status}`);
  return (await res.json()) as { predictions: { place_id: string; description: string }[] };
}

async function validateZoneAddress(
  slug: string,
  payload: { pro_place_id: number; google_place_id: string },
  token: string,
): Promise<{ address: AddressObject }> {
  const url = `${public_env.API_ENDPOINT}/public/booking/${slug}/validate-zone-address`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-cap-token': token },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Zone validation failed: ${res.status}`);
  return (await res.json()) as { address: AddressObject };
}

/** fr-FR label of a shared namespace (the widget has no next-intl provider). */
function publicLabel(namespace: string, key: string): string {
  return translate('fr-FR', namespace, key) ?? key;
}

export interface BookingRootProps {
  slug: string;
  locale: string;
  filters: BookingFilterConfig;
  /** `uuid` = pre-reservation (pro_payment) uuid, for both tunnels. */
  onCompleted: (uuid: string, tunnel: TunnelMode) => void;
  /** Étape affichée, l'étape précédente, et le rang et le total de l'étape (absents pour la confirmation). */
  onStepChange?: (step: string, info: { previousStep?: string; stepIndex?: number; stepTotal?: number }) => void;
  onError?: (code: string, message: string) => void;
  /**
   * Stripe return of the page this widget claimed (`REDIRECT_RETURN_PARAM.BOOKING` or `.RIDE`), or
   * null: only one widget may resume it, and it reopens the tab of the tunnel that started it.
   */
  claimedRedirectReturn: string | null;
  /** Tab to open first when the pro offers both (`tab` attribute, as `?mode=` on /book). */
  requestedMode?: TunnelMode;
  /** `code` attribute: promo code or gift card code applied as soon as the cart is ready. */
  prefilledCode?: string;
  /** Attribut `success-mode` : écran de succès du tunnel classique. */
  successMode: NonNullable<BookingTunnelProps['successMode']>;
}

export function BookingRoot({
  slug,
  locale,
  filters,
  onCompleted,
  onStepChange,
  claimedRedirectReturn,
  requestedMode,
  prefilledCode,
  successMode,
}: BookingRootProps): ReactElement {
  const capRef = useRef<CapInstance | null>(null);
  // Latest host callback: the element recreates it on every attribute change, which must not rebuild
  // the tunnels' callbacks (the ride tunnel would reload its config and drop the customer's choices).
  const onCompletedRef = useRef(onCompleted);
  useEffect((): void => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  // Public profile: tabs (can_book / can_ride) and suspended card. undefined = loading, null = unavailable.
  const [profile, setProfile] = useState<PublicProfessionalResponseObject | null | undefined>(undefined);
  useEffect((): (() => void) => {
    const controller = new AbortController();
    fetchProfessionalPublic(slug, { signal: controller.signal })
      .then((p): void => setProfile(p))
      .catch((): void => {
        if (!controller.signal.aborted) setProfile(null);
      });
    return (): void => controller.abort();
  }, [slug]);

  const rideReturn = claimedRedirectReturn === REDIRECT_RETURN_PARAM.RIDE;
  // Profile unavailable: classic tunnel as before — plus the ride tunnel when resuming a ride payment.
  const canRide = profile ? profile.features?.can_ride === true : rideReturn;
  const canBook = profile?.features?.can_book !== false;
  const showTabs = canBook && canRide;

  // Tab picked by the customer (tabs, « Course » card). Until then, the default tab of /book.
  const [chosenMode, setChosenMode] = useState<TunnelMode | undefined>(undefined);
  const mode: TunnelMode =
    chosenMode ??
    // Stripe return of the classic tunnel: its tab, whatever the features (as /book).
    (claimedRedirectReturn === REDIRECT_RETURN_PARAM.BOOKING
      ? 'booking'
      : resolveTunnelMode({ canBook, canRide }, rideReturn ? 'ride' : requestedMode, filters));
  // Default trip of the last « Course » card clicked in the classic tunnel.
  const [rideInitialTrip, setRideInitialTrip] = useState<RideInitialTrip | undefined>(undefined);

  // `?cid=` sur la page hôte : lien de campagne qui mène au site tiers où le widget est embarqué.
  const campaignId = useMemo(
    (): number | undefined =>
      typeof window === 'undefined' ? undefined : (readCampaignIdFromSearch(window.location.search) ?? undefined),
    [],
  );
  // Codes du lien : attribut `code`, sinon `?code=` de la page hôte (QR d'une carte cadeau, campagne), et
  // `?promo=` ; le tunnel les applique dès que le panier est prêt.
  const linkCodes = useMemo((): LinkCodes => {
    const fromPage = readLinkCodes(typeof window === 'undefined' ? '' : window.location.search);
    return { ...fromPage, ...(prefilledCode ? { code: prefilledCode } : {}) };
  }, [prefilledCode]);

  const callbacks = useMemo((): BookingCallbacks => {
    const base = createBookingCallbacks(slug, {
      // Provenance : réservation prise via le widget embarqué sur un site tiers.
      source: BookingSource.WIDGET,
      campaign_id: campaignId,
      onReservationConfirmed: (uuid): void => onCompletedRef.current(uuid, 'booking'),
    });
    return {
      ...base,
      // « Course » (RIDE) cards: only when the pro offers the ride tunnel (otherwise the tunnel hides them).
      ...(canRide
        ? {
            onSelectRidePrestation: (prestation: BookingPrestation): void => {
              setRideInitialTrip(rideInitialTripFromPrestation(prestation));
              setChosenMode('ride');
            },
          }
        : {}),
      onAutocompleteAddress: async (query: string): Promise<AddressAutocompletePrediction[]> => {
        const token = await getCaptchaToken(capRef);
        const res = await fetchAddressAutocomplete(query, token);
        return res.predictions.map(
          (p): AddressAutocompletePrediction => ({ place_id: p.place_id, description: p.description }),
        );
      },
      onValidateZoneAddress: async (proPlaceId: number, googlePlaceId: string): Promise<{ address: AddressObject }> => {
        const token = await getCaptchaToken(capRef);
        return validateZoneAddress(slug, { pro_place_id: proPlaceId, google_place_id: googlePlaceId }, token);
      },
    };
  }, [slug, canRide, campaignId]);

  const rideCallbacks = useMemo(
    (): RideCallbacks =>
      createRideCallbacks(slug, {
        source: BookingSource.WIDGET,
        campaign_id: campaignId,
        onReservationConfirmed: (uuid): void => onCompletedRef.current(uuid, 'ride'),
      }),
    [slug, campaignId],
  );

  if (profile === undefined) {
    return (
      // aria-busy: the tunnel itself is not mounted yet (tab still unknown).
      <div className="bt-root" aria-busy="true">
        <div className="bt-loading">
          <div className="bt-spinner" />
        </div>
      </div>
    );
  }

  // Semi-frozen dunning / frozen subscription: neutral « call us » card, never the reason (as /book).
  if (profile?.online_booking_suspended === true) {
    return (
      // Real slug from the profile: `slug` is usually the widget-id (UUID).
      <BookingSuspendedCard
        professionalSlug={profile.slug || slug}
        getCaptchaToken={(): Promise<string> => getCaptchaToken(capRef)}
      />
    );
  }

  const modeSwitcher = showTabs ? (
    <TunnelModeSwitch
      mode={mode}
      labels={{
        booking: publicLabel('RIDE_PUBLIC_PAGE', 'TAB_BOOKING'),
        bookingSubtitle: publicLabel('RIDE_PUBLIC_PAGE', 'TAB_BOOKING_SUBTITLE'),
        ride: publicLabel('RIDE_PUBLIC_PAGE', 'TAB_RIDE'),
        rideSubtitle: publicLabel('RIDE_PUBLIC_PAGE', 'TAB_RIDE_SUBTITLE'),
      }}
      // The default trip only belongs to the « Course » card that set it: switching back to the
      // Trajet tab by hand must not remount the tunnel with it (addresses possibly locked).
      onChange={(next: TunnelMode): void => {
        setRideInitialTrip(undefined);
        setChosenMode(next);
      }}
    />
  ) : undefined;

  if (mode === 'ride') {
    return (
      <RideTunnel
        callbacks={rideCallbacks}
        professionalName={profile?.name ?? ''}
        stripePublishableKey={public_env.STRIPE_PUBLISHABLE_KEY}
        resolveApiErrorLabel={(key: string): string | undefined => translate('fr-FR', 'API_ERRORS', key)}
        locale={locale}
        initialTrip={rideInitialTrip}
        headerAccessory={modeSwitcher}
        handleRedirectReturn={rideReturn}
        flagBaseUrl={WIDGET_FLAG_BASE_URL ?? undefined}
      />
    );
  }

  return (
    <BookingTunnel
      callbacks={callbacks}
      filters={filters}
      professionalSlug={slug}
      // Nom du pro, jamais `slug` (le widget-id, un UUID) : « Appeler … » et la demande de devis le citent.
      // Profil indisponible → libellés génériques.
      professionalName={profile?.name}
      prefilledVoucherCode={linkCodes.code}
      prefilledPromoCode={linkCodes.promo}
      locale={locale}
      stripePublishableKey={public_env.STRIPE_PUBLISHABLE_KEY}
      successMode={successMode}
      // Cross-origin embed: fetch flag SVGs from the widget host (next to
      // booking.js), not the pro's site. null → shared-components' same-origin default.
      flagBaseUrl={WIDGET_FLAG_BASE_URL ?? undefined}
      onStepChange={(step, info): void =>
        onStepChange?.(String(step), {
          previousStep: info?.previous_step,
          stepIndex: info?.step_index,
          stepTotal: info?.step_total,
        })
      }
      headerAccessory={modeSwitcher}
      handleRedirectReturn={claimedRedirectReturn === REDIRECT_RETURN_PARAM.BOOKING}
    />
  );
}
